import { useIsFocused } from '@react-navigation/native';
import { openedChannelAtom, requestedMessageFooterChannelAtom } from 'app/atoms';
import type { Channel } from 'app/types/chat';
import { useAtom } from 'jotai';
import React, { createContext, useContext, useEffect, useState } from 'react';

interface MessageFooterContextType {
  hideConvo: boolean;
  setHideConvo: (hide: boolean) => void;
  oneChannelOpen: Channel | null;
  setOneChannelOpen: (channel: Channel | null) => void;
  allChannelsOpen: boolean;
  setAllChannelsOpen: (open: boolean) => void;
}

const MessageFooterContext = createContext<MessageFooterContextType | undefined>(undefined);

export function MessageFooterProvider({ children }: { children: React.ReactNode }) {
  const [oneChannelOpen, setOneChannelOpen] = useAtom(openedChannelAtom);
  const [requestedChannel, setRequestedChannel] = useAtom(requestedMessageFooterChannelAtom);
  const [hideConvo, setHideConvo] = useState(Boolean(oneChannelOpen));
  const [allChannelsOpen, setAllChannelsOpen] = useState(false);
  const isFocused = useIsFocused();

  const activeRequest = isFocused ? requestedChannel : null;
  const [handledRequest, setHandledRequest] = useState<Channel | null>(null);
  if (activeRequest !== handledRequest) {
    setHandledRequest(activeRequest);
    if (activeRequest) setHideConvo(false);
  }

  useEffect(() => {
    if (!isFocused || !requestedChannel) return;
    setOneChannelOpen(requestedChannel);
    setRequestedChannel(null);
  }, [isFocused, requestedChannel, setOneChannelOpen, setRequestedChannel]);

  useEffect(() => {
    if (!isFocused) return;
    return () => setHideConvo(true);
  }, [isFocused]);

  return (
    <MessageFooterContext.Provider
      value={{ hideConvo, setHideConvo, oneChannelOpen, setOneChannelOpen, allChannelsOpen, setAllChannelsOpen }}
    >
      {children}
    </MessageFooterContext.Provider>
  );
}

export function useMessageFooterContext() {
  const context = useContext(MessageFooterContext);
  if (!context) {
    throw new Error('useMessageFooterContext must be used within MessageFooterProvider');
  }
  return context;
}

export function useDismissConvoPointerDown() {
  const { setHideConvo, setAllChannelsOpen } = useMessageFooterContext();
  return () => {
    setHideConvo(true);
    setAllChannelsOpen(false);
  };
}
